import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Header from '../../components/Header';
import { colors, radius, shadows } from '../../styles/theme';
import { useToast } from '../../components/Toast';
import useUrlState from '../../hooks/useUrlState';
import extractApiError from '../../utils/apiError';
import { logError } from '../../utils/logError';
import { formatTutarTR } from '../../utils/para';
import SearchableSelect from '../../components/SearchableSelect';
import { exportCsv } from '../../utils/analytics/export';

// PR-C (PC6b) — Alacak Takibi: müşteri alacaklarının vade radarı (yaşlandırma).
// /api/alacaklar/vade-radar (backend kovaları hesaplar: GECIKMIS / YEDI_GUN / OTUZ_GUN / ILERIDE).
// İstemci tarafında yeniden yaşlandırma YAPILMAZ — backend kovaları doğrudan tüketilir.
// VIEW_PAGE_SATIS ile görünür (menüde zaten enforce edilir; salt-okunur sayfa).

const API = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// Kova tanımları — sıra + başlık + aksan rengi.
const KOVA_TANIM = [
  { kod: 'GECIKMIS', baslik: 'Gecikmiş', renk: colors.danger },
  { kod: 'YEDI_GUN', baslik: '7 gün içinde', renk: colors.warning },
  { kod: 'OTUZ_GUN', baslik: '30 gün içinde', renk: '#ca8a04' },
  { kod: 'ILERIDE', baslik: 'İleride', renk: colors.success },
];

const fmtTarih = (val) => {
  if (!val) return '-';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return val;
  return d.toLocaleDateString('tr-TR');
};

const AlacakTakibiPage = () => {
  const toast = useToast();

  const [kovalar, setKovalar] = useState({});
  const [genelToplam, setGenelToplam] = useState(0);
  const [firmalar, setFirmalar] = useState([]);
  const [loading, setLoading] = useState(true);

  const [firma, setFirma] = useUrlState('alc_firma', '');

  const token = localStorage.getItem('access_token');

  // Firma master verisi (opsiyonel filtre) — bir kez yüklenir.
  useEffect(() => {
    fetch(`${API}/api/firmalar`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setFirmalar(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [token]);

  const fetchRadar = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (firma) params.set('firma', firma);
      const qs = params.toString();
      const res = await fetch(`${API}/api/alacaklar/vade-radar${qs ? `?${qs}` : ''}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(extractApiError(data.detail));
        return;
      }
      setKovalar(data.kovalar || {});
      setGenelToplam(data.genel_toplam ?? 0);
    } catch (err) {
      logError('Vade radarı yükleme hatası:', err);
      toast.error('Bağlantı hatası');
    } finally {
      setLoading(false);
    }
  }, [token, firma, toast]);

  useEffect(() => { fetchRadar(); }, [fetchRadar]);

  const firmaOptions = useMemo(
    () => [
      { value: '', label: 'Tüm Firmalar' },
      ...firmalar.filter((f) => f.aktif !== false).map((f) => ({ value: f.kod, label: f.ad })),
    ],
    [firmalar]
  );

  const radarBos = KOVA_TANIM.every((k) => ((kovalar[k.kod] || {}).kalemler || []).length === 0);

  const handleCsv = () => {
    const rows = [];
    KOVA_TANIM.forEach((kova) => {
      const kalemler = (kovalar[kova.kod] || {}).kalemler || [];
      kalemler.forEach((k) => {
        rows.push({
          Kova: kova.baslik,
          Musteri: k.cari_unvan || k.unvan || `#${k.cari_id}`,
          FaturaNo: k.fatura_no || '',
          Vade: k.vade_tarihi || '',
          KalanTutar: k.kalan_tutar ?? 0,
        });
      });
    });
    if (!rows.length) { toast.warning('Dışa aktarılacak alacak yok'); return; }
    exportCsv(rows, `alacak-takibi-${new Date().toISOString().split('T')[0]}.csv`);
  };

  if (loading && radarBos && genelToplam === 0) {
    return (
      <div>
        <Header title="Alacak Takibi" />
        <div style={{ padding: '40px', textAlign: 'center', color: colors.textSecondary }}>Yükleniyor…</div>
      </div>
    );
  }

  return (
    <div>
      <Header title="Alacak Takibi" />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        {/* Üst bar: başlık + filtre + CSV */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '20px', gap: '12px', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0, fontSize: '20px', color: colors.textPrimary }}>📊 Alacak Takibi</h2>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ minWidth: '200px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: colors.textSecondary, marginBottom: '4px', fontWeight: 'bold' }}>Firma</label>
              <SearchableSelect options={firmaOptions} value={firma} onChange={setFirma} placeholder="Tüm Firmalar" />
            </div>
            <button
              onClick={handleCsv}
              style={{ padding: '9px 16px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: colors.surface, cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', color: colors.textSecondary }}
            >
              CSV İndir
            </button>
          </div>
        </div>

        {/* KPI kartları — her kova + genel toplam */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px', marginBottom: '24px' }}>
          {KOVA_TANIM.map((kova) => {
            const k = kovalar[kova.kod] || {};
            return (
              <div key={kova.kod} style={{
                background: colors.surface, borderRadius: radius.lg, borderLeft: `4px solid ${kova.renk}`,
                padding: '16px', boxShadow: shadows.sm,
              }}>
                <div style={{ fontSize: '11px', color: colors.textTertiary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
                  {kova.baslik}
                </div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: colors.textPrimary }}>
                  {formatTutarTR(k.toplam || 0)} <span style={{ fontSize: '12px', fontWeight: 500 }}>TL</span>
                </div>
                <div style={{ fontSize: '12px', color: colors.textTertiary, marginTop: '4px' }}>
                  {k.adet || 0} fatura
                </div>
              </div>
            );
          })}
          <div style={{
            background: colors.surface, borderRadius: radius.lg, borderLeft: `4px solid ${colors.primary}`,
            padding: '16px', boxShadow: shadows.sm,
          }}>
            <div style={{ fontSize: '11px', color: colors.textTertiary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
              Genel Toplam
            </div>
            <div style={{ fontSize: '18px', fontWeight: 700, color: colors.textPrimary }}>
              {formatTutarTR(genelToplam)} <span style={{ fontSize: '12px', fontWeight: 500 }}>TL</span>
            </div>
          </div>
        </div>

        {/* Vade radar panelleri */}
        {radarBos ? (
          <div style={{ background: colors.surface, borderRadius: radius.lg, boxShadow: shadows.sm, padding: '40px', textAlign: 'center', color: colors.textTertiary }}>
            Açık alacak bulunmuyor.
          </div>
        ) : (
          <div style={{ background: colors.surface, borderRadius: radius.lg, boxShadow: shadows.sm, padding: '16px 20px' }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: colors.textPrimary, marginBottom: '14px' }}>
              🗓️ Vade Radarı <span style={{ fontSize: '12px', fontWeight: 400, color: colors.textTertiary }}>(açık alacaklar)</span>
            </div>
            {KOVA_TANIM.map((kova) => {
              const k = kovalar[kova.kod] || {};
              const kalemler = k.kalemler || [];
              if (kalemler.length === 0) return null;
              return (
                <div key={kova.kod} style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: kova.renk, display: 'inline-block' }} />
                    <span style={{ fontSize: '13px', fontWeight: 700, color: kova.renk }}>{kova.baslik}</span>
                    <span style={{ fontSize: '12px', color: colors.textTertiary }}>
                      {k.adet ?? kalemler.length} fatura · {formatTutarTR(k.toplam || 0)} TL
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {kalemler.map((kalem, i) => (
                      <div key={kalem.id ?? kalem.fatura_no ?? i} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
                        padding: '8px 12px', background: colors.backgroundAlt, borderRadius: radius.sm,
                        borderLeft: `3px solid ${kova.renk}`, flexWrap: 'wrap',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', minWidth: 0 }}>
                          <span style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                            {kalem.cari_unvan || kalem.unvan || `#${kalem.cari_id}`}
                          </span>
                          <span style={{ fontSize: '12px', fontFamily: 'monospace', color: '#1565c0' }}>
                            {kalem.fatura_no || '-'}
                          </span>
                          <span style={{ fontSize: '12px', color: colors.textSecondary }}>
                            Vade: {fmtTarih(kalem.vade_tarihi)}
                          </span>
                        </div>
                        <span style={{ fontSize: '14px', fontWeight: 700, color: colors.textPrimary, fontFamily: 'monospace' }}>
                          {formatTutarTR(kalem.kalan_tutar || 0)} TL
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

      </div>
    </div>
  );
};

export default AlacakTakibiPage;
